/* istanbul ignore file */
import { useEffect, useCallback } from 'react';
import { useAnalytics } from 'core/analytics/hooks';
import { Locales } from 'core/user/entities';
import { useServices } from './useServices';

type UseCookieConsentParams = Partial<{
  autoEnableConsent: boolean;
}>;

export const useCookieConsent = ({ autoEnableConsent = false }: UseCookieConsentParams = {}) => {
  const { trackCustomEvent } = useAnalytics();
  const { cookieConsentService, analyticsService } = useServices();

  const toggleReconsent = useCallback(() => {
    cookieConsentService.toggleReconsent();
    trackCustomEvent('funnel_screen_click', {
      screen_context: 'consent_settings',
    });
  }, [cookieConsentService, trackCustomEvent]);

  const enableAllConsent = useCallback(() => {
    analyticsService.trackEvent('cookie_consent_enabled', {
      mode: autoEnableConsent ? 'instant' : 'redirect',
    });

    cookieConsentService.enableAllConsent();
  }, [analyticsService, autoEnableConsent, cookieConsentService]);

  const getGeolocationData = useCallback(() => {
    return cookieConsentService.getGeolocationData();
  }, [cookieConsentService]);

  const maybeEnableAutoConsent = useCallback(() => {
    const { country } = getGeolocationData();

    const shouldEnable = autoEnableConsent && country === Locales.US;

    if (!shouldEnable) return;

    enableAllConsent();
  }, [autoEnableConsent, enableAllConsent, getGeolocationData]);

  useEffect(() => {
    cookieConsentService.addConsentLoadListener(maybeEnableAutoConsent);

    return () => {
      cookieConsentService.removeConsentLoadListener(maybeEnableAutoConsent);
    };
  }, [cookieConsentService, maybeEnableAutoConsent]);

  return {
    toggleReconsent,
    enableAllConsent,
    getConsentGeolocation: getGeolocationData,
  };
};
